import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, formatDate, useAuth } from '@halo/web-lib';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ImpersonateConfirmModal } from '../components/ImpersonateConfirmModal';
import {
  Store,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Cable,
  User,
  Search,
  X,
  Database,
  AlertTriangle,
  CheckCircle2,
  Copy,
  ExternalLink,
  Building2,
  Filter,
  RefreshCw,
  Download,
  Plus,
  Trash2,
  Edit3,
  Save,
  Loader2,
  UserCheck,
  Mail,
  UserPlus,
} from 'lucide-react';

import { InfoBadge } from '../components/ui/Tooltip';
import { EmailChipsInput, type EmailChip } from '../components/EmailChipsInput';
import { ImportLegacySection } from './halo-mmm/ImportLegacySection';
import shopifyLogo from './ingestion/../../assets/platforms/shopify.svg';
import metaLogo from './ingestion/../../assets/platforms/meta.svg';
import googleAdsLogo from './ingestion/../../assets/platforms/google-ads.svg';
import tiktokLogo from './ingestion/../../assets/platforms/tiktok.svg';
import tiktokShopLogo from './ingestion/../../assets/platforms/tiktok-shop.svg';
import pinterestLogo from './ingestion/../../assets/platforms/pinterest.svg';
import klaviyoLogo from './ingestion/../../assets/platforms/klaviyo.svg';
import amazonAdsLogo from './ingestion/../../assets/platforms/amazon-ads.svg';
import amazonSellerLogo from './ingestion/../../assets/platforms/amazon-seller.svg';
import ga4Logo from './ingestion/../../assets/platforms/ga4.svg';

// ─── Platform metadata ──────────────────────────

const PLATFORM_META: Record<string, { label: string; icon: string }> = {
  shopify: { label: 'Shopify', icon: shopifyLogo },
  meta: { label: 'Meta Ads', icon: metaLogo },
  google_ads: { label: 'Google Ads', icon: googleAdsLogo },
  tiktok_business: { label: 'TikTok Business', icon: tiktokLogo },
  tiktok_shop: { label: 'TikTok Shop', icon: tiktokShopLogo },
  pinterest: { label: 'Pinterest', icon: pinterestLogo },
  klaviyo: { label: 'Klaviyo', icon: klaviyoLogo },
  amazon_ads: { label: 'Amazon Ads', icon: amazonAdsLogo },
  amazon_seller: { label: 'Amazon Seller', icon: amazonSellerLogo },
  ga4: { label: 'Google Analytics', icon: ga4Logo },
};

const ROLE_STYLES: Record<string, string> = {
  OWNER:
    'bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-900/50',
  ADMIN:
    'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-900/50',
  COLLABORATOR: 'bg-teal-50 text-teal-700 border-teal-200',
  VIEWER:
    'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-800',
};

const FILTER_OPTIONS = ['All', 'With Agency', 'No Agency', 'Legacy'] as const;
type FilterOption = (typeof FILTER_OPTIONS)[number];

const SORT_OPTIONS = ['Name', 'Created', 'Integrations'] as const;
type SortOption = (typeof SORT_OPTIONS)[number];

// ─── Types ──────────────────────────────────────

interface StoreListItem {
  id: number;
  name: string;
  type: string;
  currency: string;
  subscription: string;
  legacyId: string | null;
  owner: { email: string; displayName: string | null } | null;
  integrations: string[];
  agencyId: number | null;
  agencyName: string | null;
  onboardingStep: number;
  onboardedAt: string | null;
  createdAt: string;
}

interface StoreDetail {
  id: number;
  name: string;
  type: string;
  currency: string;
  subscription: string;
  legacyId: string | null;
  agencyId: number | null;
  agencyName: string | null;
  agencyMeta: { accountManager?: string; tier?: string; notes?: string } | null;
  onboardingStep: number;
  onboardedAt: string | null;
  createdAt: string;
  members: {
    userId: string;
    email: string;
    displayName: string | null;
    role: string;
    joinedAt: string;
  }[];
  connections: {
    id: number;
    platform: string;
    externalAccountId: string;
    currency: string;
    isActive: boolean;
    tokenExpiresAt: string | null;
    createdAt: string;
  }[];
}

type InviteRole = 'admin' | 'viewer';

interface StoreInvitation {
  id: string;
  email: string;
  role: InviteRole;
  status: string;
  invitedBy: string;
  inviterName: string | null;
  expiresAt: string;
  createdAt: string;
  acceptedAt: string | null;
}

interface ConnectionDetail {
  id: number;
  uuid: string;
  platform: string;
  externalAccountId: string;
  currency: string;
  isActive: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  tokenExpiresAt: string | null;
  metadata: Record<string, string | boolean | number | null>;
  createdAt: string;
  updatedAt: string;
}

interface ConnectionPatchBody {
  externalAccountId?: string;
  currency?: string;
  isActive?: boolean;
  metadata?: Record<string, string | boolean | number | null>;
}

interface ConnectionCreateBody {
  platform: string;
  accessToken: string;
  refreshToken?: string;
  externalAccountId?: string;
  currency?: string;
}

const PLATFORM_KEYS = [
  'shopify',
  'meta',
  'google_ads',
  'tiktok_business',
  'tiktok_shop',
  'pinterest',
  'klaviyo',
  'amazon_ads',
  'amazon_seller',
  'ga4',
] as const;

type PlatformKey = (typeof PLATFORM_KEYS)[number];

// ─── Helpers ────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function tokenStatus(expiresAt: string | null): { label: string; className: string } | null {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0)
    return {
      label: 'Expired',
      className:
        'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-900/50',
    };
  if (diff < 7 * 24 * 60 * 60 * 1000)
    return {
      label: `Expires ${timeAgo(new Date(Date.now() + diff).toISOString()).replace(' ago', '')}`,
      className:
        'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900/50',
    };
  return null;
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

// ═══════════════════════════════════════════════
// Store List View
// ═══════════════════════════════════════════════

export function StoreListView() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterOption>('All');
  const [sortBy, setSortBy] = useState<SortOption>('Name');
  const [focusIdx, setFocusIdx] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);
  const [pending, setPending] = useState<{ id: number; name: string } | null>(null);
  const { impersonation } = useAuth();

  const { data: stores, isLoading } = useQuery({
    queryKey: ['admin', 'stores'],
    queryFn: async ({ signal }) => {
      const res = await api.get<StoreListItem[]>('/api/v1/admin/stores', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load stores');
      return res.data;
    },
  });

  const summary = useMemo(() => {
    const all = stores ?? [];
    return {
      total: all.length,
      connected: all.filter((s) => s.integrations.length > 0).length,
      withAgency: all.filter((s) => s.agencyName).length,
      legacy: all.filter((s) => s.legacyId).length,
    };
  }, [stores]);

  const resultStores = useMemo(() => {
    let list = [...(stores ?? [])];

    if (filter === 'With Agency') list = list.filter((s) => s.agencyName);
    else if (filter === 'No Agency') list = list.filter((s) => !s.agencyName);
    else if (filter === 'Legacy') list = list.filter((s) => s.legacyId);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          String(s.id).includes(q) ||
          (s.owner?.email.toLowerCase().includes(q) ?? false) ||
          (s.agencyName?.toLowerCase().includes(q) ?? false),
      );
    }

    if (sortBy === 'Created') {
      list.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    } else if (sortBy === 'Integrations') {
      list.sort((a, b) => b.integrations.length - a.integrations.length);
    } else {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }

    return list;
  }, [stores, search, filter, sortBy]);

  // Keyboard navigation
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (resultStores.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIdx((p) => Math.min(p + 1, resultStores.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIdx((p) => Math.max(p - 1, 0));
      } else if (e.key === 'Enter' && focusIdx >= 0)
        navigate(`/stores/${resultStores[focusIdx]!.id}`);
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [resultStores, focusIdx, navigate]);

  useEffect(() => {
    if (focusIdx < 0) return;
    listRef.current
      ?.querySelector(`[data-idx="${focusIdx}"]`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [focusIdx]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-0.5">Stores</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {summary.total} stores
            {summary.connected > 0 && (
              <span className="text-green-600 dark:text-green-400">
                {' '}
                &middot; {summary.connected} connected
              </span>
            )}
            {summary.withAgency > 0 && (
              <span className="text-indigo-600 dark:text-indigo-400">
                {' '}
                &middot; {summary.withAgency} agency-managed
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { label: 'Total', value: summary.total, color: 'text-gray-900 dark:text-gray-100' },
          {
            label: 'Connected',
            value: summary.connected,
            color: 'text-green-600 dark:text-green-400',
          },
          {
            label: 'Agency',
            value: summary.withAgency,
            color: 'text-indigo-600 dark:text-indigo-400',
            filterTo: 'With Agency' as const,
          },
          {
            label: 'Legacy',
            value: summary.legacy,
            color: summary.legacy > 0 ? 'text-amber-600' : 'text-gray-400 dark:text-gray-500',
            filterTo: 'Legacy' as const,
          },
        ].map((card) => (
          <button
            key={card.label}
            onClick={() =>
              'filterTo' in card && card.filterTo
                ? setFilter(filter === card.filterTo ? 'All' : card.filterTo)
                : undefined
            }
            className={`bg-white dark:bg-gray-900 border rounded-lg px-3 py-2 text-left transition-all hover:shadow-sm ${
              'filterTo' in card && filter === card.filterTo
                ? 'border-gray-900 ring-1 ring-gray-900'
                : 'border-gray-200'
            }`}
          >
            <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {card.label}
            </div>
            <div className={`text-lg font-semibold ${card.color}`}>{card.value}</div>
          </button>
        ))}
      </div>

      {/* Search + Filter + Sort */}
      {(stores ?? []).length > 0 && (
        <div className="flex items-center gap-3 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, ID, owner, or agency..."
              className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Filter className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt}
                onClick={() => setFilter(opt)}
                className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                  filter === opt
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'border-gray-200 text-gray-500 dark:text-gray-400 hover:bg-gray-50'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="text-xs border border-gray-200 dark:border-gray-800 rounded-lg px-2 py-1.5 text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-900"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                Sort: {opt}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Store List */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 animate-pulse"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (stores ?? []).length === 0 ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-12 text-center">
          <Database className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No stores found.</p>
        </div>
      ) : (
        <div ref={listRef} className="space-y-1.5">
          {resultStores.map((s, idx) => {
            const isFocused = focusIdx === idx;
            return (
              <button
                key={s.id}
                data-idx={idx}
                onClick={() => navigate(`/stores/${s.id}`)}
                className={`w-full text-left bg-white dark:bg-gray-900 rounded-xl border px-4 py-3 hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-sm transition-all ${
                  isFocused
                    ? 'ring-2 ring-gray-900 ring-offset-1 border-gray-300'
                    : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
                      <Store className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {s.name}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">#{s.id}</span>
                        {s.legacyId && (
                          <InfoBadge
                            tip={`Migrated from legacy GrowHalo. Legacy ID: ${s.legacyId}. Some historical data may only exist in the legacy system.`}
                          >
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-600 border border-amber-200 dark:border-amber-900/50">
                              legacy
                            </span>
                          </InfoBadge>
                        )}
                        {s.agencyName && (
                          <InfoBadge
                            tip={`Managed by ${s.agencyName}. Agency clients may have different billing and access rules.`}
                          >
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-900/50">
                              {s.agencyName}
                            </span>
                          </InfoBadge>
                        )}
                        {s.onboardedAt ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 dark:bg-green-950/40 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-900/50">
                            onboarded
                          </span>
                        ) : (
                          <InfoBadge
                            tip={`Onboarding step ${s.onboardingStep}/4. Store cannot access dashboard until onboarding is complete.`}
                          >
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-600 border border-amber-200 dark:border-amber-900/50">
                              step {s.onboardingStep}/4
                            </span>
                          </InfoBadge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <span>{s.currency}</span>
                        <span>&middot;</span>
                        <span>{s.owner?.email ?? 'No owner'}</span>
                      </div>
                      {/* Platform icons */}
                      {s.integrations.length > 0 && (
                        <div className="flex items-center gap-1 mt-1.5">
                          {s.integrations.map((p) => {
                            const pm = PLATFORM_META[p];
                            return pm?.icon ? (
                              <img
                                key={p}
                                src={pm.icon}
                                alt={pm.label}
                                title={pm.label}
                                className="w-4 h-4"
                              />
                            ) : (
                              <span
                                key={p}
                                className="text-[9px] px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                              >
                                {p}
                              </span>
                            );
                          })}
                          <InfoBadge tip="Number of external platforms connected via OAuth. Each connection enables data ingestion for that platform.">
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1">
                              {s.integrations.length} platforms
                            </span>
                          </InfoBadge>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!impersonation && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPending({ id: s.id, name: s.name });
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-amber-200 dark:border-amber-900/50 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40 transition-colors"
                      >
                        <UserCheck className="w-3 h-3" />
                        Impersonate
                      </button>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </div>
                </div>
              </button>
            );
          })}
          {resultStores.length === 0 && (search || filter !== 'All') && (
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center">
              <p className="text-sm text-gray-400 dark:text-gray-500">
                No stores matching your filters
              </p>
              <button
                onClick={() => {
                  setSearch('');
                  setFilter('All');
                }}
                className="text-xs text-blue-600 dark:text-blue-400 mt-1 hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Import Legacy Stores */}
      <div className="mt-6">
        <ImportLegacySection />
      </div>

      {pending && (
        <ImpersonateConfirmModal
          storeId={pending.id}
          storeName={pending.name}
          onClose={() => setPending(null)}
          onStarted={() => {
            window.location.href = 'https://dashboard.growhalo.ai';
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════
// Store Detail View
// ═══════════════════════════════════════════════

export function StoreDetailView() {
  const { id } = useParams<{ id: string }>();
  const storeId = Number(id);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{ id: number; name: string } | null>(null);
  const { impersonation } = useAuth();

  const { data: store, isLoading } = useQuery({
    queryKey: ['admin', 'stores', storeId],
    queryFn: async ({ signal }) => {
      const res = await api.get<StoreDetail>(`/api/v1/admin/stores/${storeId}`, { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load store');
      return res.data;
    },
  });

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  function startEditName() {
    if (!store) return;
    setNameDraft(store.name);
    setNameError(null);
    setEditingName(true);
  }

  const renameMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await api.patch<{ id: number; name: string }>(`/api/v1/admin/stores/${storeId}`, {
        name,
      });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to update store name');
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stores'] });
      setEditingName(false);
      setNameError(null);
    },
    onError: (err: Error) => setNameError(err.message),
  });

  function handleSaveName() {
    const trimmed = nameDraft.trim();
    if (trimmed.length < 1 || trimmed.length > 120) {
      setNameError('Name must be between 1 and 120 characters');
      return;
    }
    if (store && trimmed === store.name) {
      setEditingName(false);
      return;
    }
    renameMutation.mutate(trimmed);
  }

  function handleCopyId() {
    copyToClipboard(String(storeId));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await api.delete(`/api/v1/admin/stores/${storeId}`);
      if (!res.success) throw new Error(res.error ?? 'Failed to delete store');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stores'] });
      navigate('/stores');
    },
  });

  function handleDelete() {
    if (!store) return;
    if (
      !window.confirm(
        `Permanently delete "${store.name}" and all its connections? This cannot be undone.`,
      )
    )
      return;
    deleteMutation.mutate();
  }

  return (
    <div>
      <Link
        to="/stores"
        className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4"
      >
        <ChevronLeft className="w-4 h-4" /> Back to stores
      </Link>

      {deleteMutation.error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg text-sm text-red-700 dark:text-red-400">
          {deleteMutation.error.message}
        </div>
      )}

      {isLoading || !store ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-4" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Store Header */}
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                {editingName ? (
                  <div className="mb-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveName();
                          if (e.key === 'Escape') {
                            setEditingName(false);
                            setNameError(null);
                          }
                        }}
                        autoFocus
                        maxLength={120}
                        className="flex-1 text-lg font-semibold text-gray-900 dark:text-gray-100 px-2 py-1 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                      />
                      <button
                        onClick={handleSaveName}
                        disabled={renameMutation.isPending}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-gray-900 dark:bg-gray-950 text-white hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50"
                      >
                        {renameMutation.isPending ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Save className="w-3 h-3" />
                        )}
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingName(false);
                          setNameError(null);
                        }}
                        disabled={renameMutation.isPending}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-950"
                      >
                        <X className="w-3 h-3" />
                        Cancel
                      </button>
                    </div>
                    {nameError && (
                      <div className="text-xs text-red-600 dark:text-red-400 mt-1">{nameError}</div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {store.name}
                    </h3>
                    <button
                      type="button"
                      onClick={startEditName}
                      title="Edit store name"
                      className="p-1 rounded text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={handleCopyId}
                    className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    title="Copy store ID"
                  >
                    <Copy className="w-3 h-3" />
                    {copied ? 'Copied!' : `ID: ${store.id}`}
                  </button>
                  {store.legacyId && (
                    <InfoBadge
                      tip={`Migrated from legacy GrowHalo platform. Legacy ID: ${store.legacyId}`}
                    >
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-600 border border-amber-200 dark:border-amber-900/50">
                        Legacy: {store.legacyId}
                      </span>
                    </InfoBadge>
                  )}
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                  Created {formatDate(store.createdAt)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!impersonation && (
                  <button
                    type="button"
                    onClick={() => setPending({ id: store.id, name: store.name })}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-amber-300 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-950/40 transition-colors"
                  >
                    <UserCheck className="w-3.5 h-3.5" />
                    Impersonate
                  </button>
                )}
                <button
                  onClick={() => navigate(`/ingestion/${storeId}`)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Ingestion
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-50 transition-colors"
                >
                  {deleteMutation.isPending ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                  Delete Store
                </button>
              </div>
            </div>
          </div>

          {/* Metadata Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <MetaCard label="Type" value={store.type} />
            <MetaCard label="Currency" value={store.currency} />
            <MetaCard label="Subscription" value={store.subscription} />
            <MetaCard label="Connections" value={String(store.connections.length)} />
          </div>

          {/* Onboarding Control */}
          <OnboardingControl
            storeId={storeId}
            step={store.onboardingStep}
            onboardedAt={store.onboardedAt}
          />

          {/* Agency Info */}
          {store.agencyName && (
            <div className="bg-indigo-50 dark:bg-indigo-950/40 rounded-xl border border-indigo-200 dark:border-indigo-900/50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-indigo-500" />
                <span className="text-xs font-medium text-indigo-700 dark:text-indigo-400">
                  Agency: {store.agencyName}
                </span>
              </div>
              {store.agencyMeta && (
                <div className="grid grid-cols-3 gap-3 text-xs">
                  {store.agencyMeta.accountManager && (
                    <div>
                      <span className="text-indigo-400">Manager:</span>{' '}
                      <span className="text-indigo-700 dark:text-indigo-400 font-medium">
                        {store.agencyMeta.accountManager}
                      </span>
                    </div>
                  )}
                  {store.agencyMeta.tier && (
                    <div>
                      <span className="text-indigo-400">Tier:</span>{' '}
                      <span className="text-indigo-700 dark:text-indigo-400 font-medium">
                        {store.agencyMeta.tier}
                      </span>
                    </div>
                  )}
                  {store.agencyMeta.notes && (
                    <div className="col-span-3">
                      <span className="text-indigo-400">Notes:</span>{' '}
                      <span className="text-indigo-600 dark:text-indigo-400">
                        {store.agencyMeta.notes}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Members */}
          <MembersCard storeId={storeId} members={store.members} />

          {/* Connections */}
          <ConnectionsManager storeId={storeId} />
        </div>
      )}

      {pending && (
        <ImpersonateConfirmModal
          storeId={pending.id}
          storeName={pending.name}
          onClose={() => setPending(null)}
          onStarted={() => {
            window.location.href = 'https://dashboard.growhalo.ai';
          }}
        />
      )}
    </div>
  );
}

// ─── Members Card (with invite + pending invitations) ─────

function MembersCard({ storeId, members }: { storeId: number; members: StoreDetail['members'] }) {
  const queryClient = useQueryClient();
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteChips, setInviteChips] = useState<EmailChip[]>([]);
  const [inviteRole, setInviteRole] = useState<InviteRole>('viewer');
  const [lastSkipped, setLastSkipped] = useState<
    Array<{ email: string; reason: 'already_member' | 'invalid_email' | 'error'; message?: string }>
  >([]);
  const [feedback, setFeedback] = useState<{
    id: string;
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  function showFeedback(type: 'success' | 'error', message: string) {
    const id = String(Date.now());
    setFeedback({ id, type, message });
    setTimeout(() => setFeedback((prev) => (prev?.id === id ? null : prev)), 3000);
  }

  const { data: invitations, isLoading: invitesLoading } = useQuery({
    queryKey: ['admin', 'stores', storeId, 'invitations'],
    queryFn: async ({ signal }) => {
      const res = await api.get<StoreInvitation[]>(`/api/v1/admin/stores/${storeId}/invitations`, {
        signal,
      });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load invitations');
      return res.data;
    },
  });

  type SkipReason = 'already_member' | 'invalid_email' | 'error';
  interface BulkInviteResponse {
    created: Array<{ id: string; email: string }>;
    skipped: Array<{ email: string; reason: SkipReason; message?: string }>;
    summary: { requested: number; created: number; skipped: number };
  }

  const bulkInviteMutation = useMutation({
    mutationFn: async ({ emails, role }: { emails: string[]; role: InviteRole }) => {
      const res = await api.post<BulkInviteResponse>(
        `/api/v1/admin/stores/${storeId}/invitations/bulk`,
        { emails, role },
      );
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to send invitations');
      return res.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'stores', storeId, 'invitations'],
      });
      const { summary, skipped } = data;
      const msg =
        skipped.length === 0
          ? `${summary.created} invitation${summary.created === 1 ? '' : 's'} sent`
          : `${summary.created} sent · ${summary.skipped} skipped`;
      showFeedback('success', msg);
      setLastSkipped(skipped);
      if (skipped.length === 0) {
        setInviteChips([]);
        setInviteRole('viewer');
        setShowInviteForm(false);
      }
    },
    onError: (err: Error) => showFeedback('error', err.message),
  });

  const changeRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const res = await api.put(`/api/v1/admin/stores/${storeId}/members/${userId}/role`, { role });
      if (!res.success) throw new Error(res.error ?? 'Failed to change role');
      return res;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId] });
      showFeedback('success', 'Role updated');
    },
    onError: (err: Error) => showFeedback('error', err.message),
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.delete(`/api/v1/admin/stores/${storeId}/members/${userId}`);
      if (!res.success) throw new Error(res.error ?? 'Failed to remove member');
      return res;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId] });
      showFeedback('success', 'Member removed');
    },
    onError: (err: Error) => showFeedback('error', err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const res = await api.delete(`/api/v1/admin/stores/${storeId}/invitations/${invitationId}`);
      if (!res.success) throw new Error(res.error ?? 'Failed to cancel invitation');
      return res;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId, 'invitations'] });
      showFeedback('success', 'Invitation cancelled');
    },
    onError: (err: Error) => showFeedback('error', err.message),
  });

  const validChips = inviteChips.filter((c) => c.valid);
  const hasInvalid = inviteChips.some((c) => !c.valid);

  function handleSubmitInvite(e: React.FormEvent) {
    e.preventDefault();
    if (validChips.length === 0 || hasInvalid) return;
    setLastSkipped([]);
    bulkInviteMutation.mutate({
      emails: validChips.map((c) => c.email),
      role: inviteRole,
    });
  }

  const pendingCount = invitations?.length ?? 0;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Members ({members.length})
          </h4>
          {pendingCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-900/50">
              {pendingCount} pending
            </span>
          )}
        </div>
        {!showInviteForm && (
          <button
            onClick={() => setShowInviteForm(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950 transition-colors"
          >
            <UserPlus className="w-3.5 h-3.5" />
            Invite
          </button>
        )}
      </div>

      {feedback && (
        <div
          className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg mb-3 ${
            feedback.type === 'success'
              ? 'bg-green-50 text-green-700 dark:text-green-400 border border-green-200'
              : 'bg-red-50 text-red-700 dark:text-red-400 border border-red-200'
          }`}
        >
          {feedback.type === 'success' ? (
            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          ) : (
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          )}
          {feedback.message}
        </div>
      )}

      {showInviteForm && (
        <form
          onSubmit={handleSubmitInvite}
          className="border border-gray-200 dark:border-gray-800 rounded-lg p-3 mb-4 bg-gray-50 dark:bg-gray-950 space-y-2"
        >
          <div className="space-y-2">
            <label className="block">
              <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Emails
              </span>
              <div className="mt-0.5">
                <EmailChipsInput
                  value={inviteChips}
                  onChange={setInviteChips}
                  disabled={bulkInviteMutation.isPending}
                  autoFocus
                />
              </div>
              <span className="block mt-1 text-[10px] text-gray-500 dark:text-gray-400">
                Press comma or Enter to add. Up to 50 addresses.
              </span>
            </label>
            <label className="block">
              <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Role
              </span>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as InviteRole)}
                disabled={bulkInviteMutation.isPending}
                className="mt-0.5 w-full text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
              >
                <option value="viewer">Viewer</option>
                <option value="admin">Admin</option>
              </select>
            </label>
          </div>

          {lastSkipped.length > 0 && (
            <div className="border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/40 rounded-lg p-2 text-[11px] text-amber-800 dark:text-amber-200 space-y-0.5">
              <div className="font-medium">Skipped:</div>
              <ul className="space-y-0.5">
                {lastSkipped.map((s) => (
                  <li key={s.email}>
                    <span className="font-mono">{s.email}</span> —{' '}
                    {s.reason === 'already_member'
                      ? 'already a member'
                      : s.reason === 'invalid_email'
                        ? 'invalid email'
                        : (s.message ?? 'error')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="submit"
              disabled={bulkInviteMutation.isPending || validChips.length === 0 || hasInvalid}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-900 dark:bg-gray-950 text-white hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
            >
              {bulkInviteMutation.isPending ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Mail className="w-3 h-3" />
              )}
              {validChips.length > 1 ? `Send ${validChips.length} invitations` : 'Send invitation'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowInviteForm(false);
                setInviteChips([]);
                setInviteRole('viewer');
                setLastSkipped([]);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X className="w-3 h-3" />
              Cancel
            </button>
          </div>
        </form>
      )}

      {members.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">No members</p>
      ) : (
        <div className="space-y-2">
          {members.map((m) => {
            const roleKey = m.role.toUpperCase();
            const tip =
              roleKey === 'OWNER'
                ? 'Full access including billing, member management, and store deletion.'
                : roleKey === 'ADMIN'
                  ? 'Can manage integrations, view all data, and configure ingestion. Cannot delete the store.'
                  : roleKey === 'COLLABORATOR'
                    ? 'Can view dashboards and reports. Cannot modify integrations or settings.'
                    : 'Read-only access to dashboards. Cannot modify any settings.';
            const isPendingRole =
              changeRoleMutation.isPending && changeRoleMutation.variables?.userId === m.userId;
            const isPendingRemove =
              removeMemberMutation.isPending && removeMemberMutation.variables === m.userId;
            return (
              <div
                key={m.userId}
                className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800 last:border-0"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {m.email}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {m.displayName && <span>{m.displayName}</span>}
                    <span>Joined {timeAgo(m.joinedAt)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <InfoBadge tip={tip}>
                    <select
                      value={m.role}
                      disabled={isPendingRole || isPendingRemove}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (next === m.role) return;
                        if (
                          next === 'owner' &&
                          !window.confirm(
                            `Promote ${m.email} to OWNER? They will have full control of this store.`,
                          )
                        )
                          return;
                        if (
                          m.role === 'owner' &&
                          !window.confirm(
                            `Demote OWNER ${m.email} to ${next}? Make sure another owner exists.`,
                          )
                        )
                          return;
                        changeRoleMutation.mutate({ userId: m.userId, role: next });
                      }}
                      className={`text-[10px] px-2 py-0.5 rounded-full border font-medium uppercase cursor-pointer disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-gray-900 ${ROLE_STYLES[roleKey] ?? ROLE_STYLES['VIEWER']}`}
                    >
                      <option value="owner">Owner</option>
                      <option value="admin">Admin</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </InfoBadge>
                  <button
                    onClick={() => {
                      if (window.confirm(`Remove ${m.email} from this store?`)) {
                        removeMemberMutation.mutate(m.userId);
                      }
                    }}
                    disabled={isPendingRole || isPendingRemove}
                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50 transition-colors"
                    title="Remove member"
                  >
                    {isPendingRemove ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pending Invitations */}
      {(invitesLoading || pendingCount > 0) && (
        <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-3.5 h-3.5 text-amber-500" />
            <h5 className="text-xs font-medium text-gray-700 dark:text-gray-200 uppercase tracking-wide">
              Pending Invitations
            </h5>
          </div>
          {invitesLoading ? (
            <div className="flex items-center justify-center py-2 text-gray-400 dark:text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : (
            <div className="space-y-1.5">
              {(invitations ?? []).map((inv) => {
                const expired = new Date(inv.expiresAt).getTime() < Date.now();
                return (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between py-1.5 px-2 rounded-lg bg-amber-50/40 border border-amber-100"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-gray-900 dark:text-gray-100 truncate">
                        {inv.email}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        <span
                          className={`px-1.5 py-0.5 rounded-full border font-medium ${ROLE_STYLES[inv.role.toUpperCase()] ?? ROLE_STYLES['VIEWER']}`}
                        >
                          {inv.role}
                        </span>
                        <span>Invited {timeAgo(inv.createdAt)}</span>
                        {expired && <span className="text-red-500">&middot; Expired</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        if (window.confirm(`Cancel invitation to ${inv.email}?`)) {
                          cancelMutation.mutate(inv.id);
                        }
                      }}
                      disabled={cancelMutation.isPending}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-50 transition-colors"
                    >
                      <X className="w-3 h-3" />
                      Cancel
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Connections Manager ────────────────────────

function ConnectionsManager({ storeId }: { storeId: number }) {
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [feedback, setFeedback] = useState<{
    id: string;
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  function showFeedback(type: 'success' | 'error', message: string) {
    const id = String(Date.now());
    setFeedback({ id, type, message });
    setTimeout(() => setFeedback((prev) => (prev?.id === id ? null : prev)), 3000);
  }

  const { data: connections, isLoading } = useQuery({
    queryKey: ['admin', 'stores', storeId, 'connections'],
    queryFn: async ({ signal }) => {
      const res = await api.get<ConnectionDetail[]>(`/api/v1/admin/stores/${storeId}/connections`, {
        signal,
      });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load connections');
      return res.data;
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ connectionId, isActive }: { connectionId: number; isActive: boolean }) => {
      const res = await api.patch(`/api/v1/admin/stores/${storeId}/connections/${connectionId}`, {
        isActive,
      });
      if (!res.success) throw new Error(res.error ?? 'Failed to toggle');
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId, 'connections'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId] });
      showFeedback('success', 'Connection status updated');
    },
    onError: (err: Error) => showFeedback('error', err.message),
  });

  const refreshMutation = useMutation({
    mutationFn: async (connectionId: number) => {
      const res = await api.post(
        `/api/v1/admin/stores/${storeId}/connections/${connectionId}/refresh`,
        {},
      );
      if (!res.success) throw new Error(res.error ?? 'Failed to refresh token');
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId, 'connections'] });
      showFeedback('success', 'Token refreshed successfully');
    },
    onError: (err: Error) => showFeedback('error', err.message),
  });

  const reimportMutation = useMutation({
    mutationFn: async (platform: string) => {
      const res = await api.post(`/api/v1/admin/stores/${storeId}/connections/reimport`, {
        platform,
      });
      if (!res.success) throw new Error(res.error ?? 'Failed to reimport credentials');
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId, 'connections'] });
      showFeedback('success', 'Credentials reimported from legacy DB');
    },
    onError: (err: Error) => showFeedback('error', err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ connectionId, hard }: { connectionId: number; hard: boolean }) => {
      const url = hard
        ? `/api/v1/admin/stores/${storeId}/connections/${connectionId}?hard=true`
        : `/api/v1/admin/stores/${storeId}/connections/${connectionId}`;
      const res = await api.delete(url);
      if (!res.success) throw new Error(res.error ?? 'Failed to delete connection');
      return { hard };
    },
    onSuccess: ({ hard }) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId, 'connections'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId] });
      setExpandedId(null);
      setEditingId(null);
      showFeedback('success', hard ? 'Connection permanently deleted' : 'Connection removed');
    },
    onError: (err: Error) => showFeedback('error', err.message),
  });

  const patchMutation = useMutation({
    mutationFn: async ({
      connectionId,
      body,
    }: {
      connectionId: number;
      body: ConnectionPatchBody;
    }) => {
      const res = await api.patch(
        `/api/v1/admin/stores/${storeId}/connections/${connectionId}`,
        body,
      );
      if (!res.success) throw new Error(res.error ?? 'Failed to update connection');
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId, 'connections'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId] });
      setEditingId(null);
      showFeedback('success', 'Connection updated');
    },
    onError: (err: Error) => showFeedback('error', err.message),
  });

  const createMutation = useMutation({
    mutationFn: async (body: ConnectionCreateBody) => {
      const res = await api.post(`/api/v1/admin/stores/${storeId}/connections`, body);
      if (!res.success) throw new Error(res.error ?? 'Failed to create connection');
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId, 'connections'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId] });
      setShowCreateForm(false);
      showFeedback('success', 'Connection created');
    },
    onError: (err: Error) => showFeedback('error', err.message),
  });

  const connectionCount = connections?.length ?? 0;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Cable className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Connections ({connectionCount})
          </h4>
        </div>
      </div>

      {/* Feedback banner */}
      {feedback && (
        <div
          className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg mb-3 ${
            feedback.type === 'success'
              ? 'bg-green-50 text-green-700 dark:text-green-400 border border-green-200'
              : 'bg-red-50 text-red-700 dark:text-red-400 border border-red-200'
          }`}
        >
          {feedback.type === 'success' ? (
            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          ) : (
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          )}
          {feedback.message}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-gray-400 dark:text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : connectionCount === 0 && !showCreateForm ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 mb-3">No connections</p>
      ) : (
        <div className="space-y-1.5 mb-3">
          {(connections ?? []).map((c) => (
            <ConnectionRow
              key={c.id}
              connection={c}
              storeId={storeId}
              isExpanded={expandedId === c.id}
              isEditing={editingId === c.id}
              onToggleExpand={() => {
                setExpandedId(expandedId === c.id ? null : c.id);
                if (editingId === c.id) setEditingId(null);
              }}
              onStartEdit={() => setEditingId(c.id)}
              onCancelEdit={() => setEditingId(null)}
              onSave={(body) => patchMutation.mutate({ connectionId: c.id, body })}
              onToggleActive={() =>
                toggleActiveMutation.mutate({ connectionId: c.id, isActive: !c.isActive })
              }
              onRefresh={() => refreshMutation.mutate(c.id)}
              onDelete={() => {
                if (
                  window.confirm(
                    `Remove ${PLATFORM_META[c.platform]?.label ?? c.platform} connection? It will be soft-deleted (recoverable).`,
                  )
                ) {
                  deleteMutation.mutate({ connectionId: c.id, hard: false });
                }
              }}
              onHardDelete={() => {
                if (
                  window.confirm(
                    `Permanently delete ${PLATFORM_META[c.platform]?.label ?? c.platform} connection?\n\nThis bypasses soft-delete and removes the row entirely. Use this if the store needs to reconnect and is hitting "uq_connection_store_platform" errors.\n\nThis cannot be undone.`,
                  )
                ) {
                  deleteMutation.mutate({ connectionId: c.id, hard: true });
                }
              }}
              onReimport={() => {
                if (
                  window.confirm(
                    `Re-import ${PLATFORM_META[c.platform]?.label ?? c.platform} credentials from the legacy database? This will overwrite current tokens.`,
                  )
                ) {
                  reimportMutation.mutate(c.platform);
                }
              }}
              isSaving={patchMutation.isPending}
              isRefreshing={refreshMutation.isPending}
              isReimporting={reimportMutation.isPending}
              isDeleting={deleteMutation.isPending}
              isToggling={toggleActiveMutation.isPending}
            />
          ))}
        </div>
      )}

      {/* Create Connection Form */}
      {showCreateForm ? (
        <CreateConnectionForm
          onSubmit={(body) => createMutation.mutate(body)}
          onCancel={() => setShowCreateForm(false)}
          isPending={createMutation.isPending}
        />
      ) : (
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Connection
        </button>
      )}
    </div>
  );
}

// ─── Connection Row ─────────────────────────────

interface ConnectionRowProps {
  connection: ConnectionDetail;
  storeId: number;
  isExpanded: boolean;
  isEditing: boolean;
  onToggleExpand: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (body: ConnectionPatchBody) => void;
  onToggleActive: () => void;
  onRefresh: () => void;
  onReimport: () => void;
  onDelete: () => void;
  onHardDelete: () => void;
  isSaving: boolean;
  isRefreshing: boolean;
  isReimporting: boolean;
  isDeleting: boolean;
  isToggling: boolean;
}

function ConnectionRow({
  connection: c,
  storeId,
  isExpanded,
  isEditing,
  onToggleExpand,
  onStartEdit,
  onCancelEdit,
  onSave,
  onToggleActive,
  onRefresh,
  onReimport,
  onDelete,
  onHardDelete,
  isSaving,
  isRefreshing,
  isReimporting,
  isDeleting,
  isToggling,
}: ConnectionRowProps) {
  const [showReselect, setShowReselect] = useState(false);
  const pm = PLATFORM_META[c.platform];
  const token = tokenStatus(c.tokenExpiresAt);

  const [editFields, setEditFields] = useState({
    externalAccountId: c.externalAccountId,
    currency: c.currency,
    metadata: { ...c.metadata },
  });

  // Reset edit fields when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setEditFields({
        externalAccountId: c.externalAccountId,
        currency: c.currency,
        metadata: { ...c.metadata },
      });
    }
  }, [isEditing, c.externalAccountId, c.currency, c.metadata]);

  function handleSave() {
    onSave({
      externalAccountId: editFields.externalAccountId,
      currency: editFields.currency,
      metadata: editFields.metadata,
    });
  }

  function updateMetaField(key: string, value: string | boolean) {
    setEditFields((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, [key]: value },
    }));
  }

  const isGoogleAds = c.platform === 'google_ads';

  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
      {/* Header row */}
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-950 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-800 flex items-center justify-center bg-white dark:bg-gray-900 shrink-0">
            {pm?.icon ? (
              <img src={pm.icon} alt={pm.label} className="w-4.5 h-4.5" />
            ) : (
              <Cable className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            )}
          </div>
          <div className="text-left">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {pm?.label ?? c.platform}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {c.externalAccountId} &middot; {c.currency}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {token && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${token.className}`}
            >
              {token.label}
            </span>
          )}
          <span
            className={`inline-flex items-center gap-1 text-xs ${c.isActive ? 'text-green-600' : 'text-red-500'}`}
          >
            <span
              className={`w-2 h-2 rounded-full ${c.isActive ? 'bg-green-500' : 'bg-red-400'}`}
            />
            {c.isActive ? 'Active' : 'Inactive'}
          </span>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          )}
        </div>
      </button>

      {/* Expanded details */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-3 bg-gray-50 dark:bg-gray-950 space-y-3">
          {/* Read-only details */}
          {!isEditing && (
            <>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                <DetailRow label="UUID" value={c.uuid} />
                <DetailRow label="External Account ID" value={c.externalAccountId} />
                <DetailRow label="Currency" value={c.currency} />
                <DetailRow
                  label="Token Expires"
                  value={c.tokenExpiresAt ? formatDate(c.tokenExpiresAt) : 'N/A'}
                />
                <DetailRow label="Created" value={formatDate(c.createdAt)} />
                <DetailRow label="Updated" value={formatDate(c.updatedAt)} />
              </div>

              {/* Metadata */}
              {Object.keys(c.metadata).length > 0 && (
                <div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    Metadata
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                    {Object.entries(c.metadata).map(([key, val]) => (
                      <DetailRow key={key} label={key} value={String(val ?? '')} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Edit form */}
          {isEditing && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    External Account ID
                  </span>
                  <input
                    type="text"
                    value={editFields.externalAccountId}
                    onChange={(e) =>
                      setEditFields((prev) => ({ ...prev, externalAccountId: e.target.value }))
                    }
                    className="mt-0.5 w-full text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Currency
                  </span>
                  <input
                    type="text"
                    value={editFields.currency}
                    onChange={(e) =>
                      setEditFields((prev) => ({ ...prev, currency: e.target.value }))
                    }
                    className="mt-0.5 w-24 text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
                  />
                </label>
              </div>

              {/* Google Ads specific fields */}
              {isGoogleAds && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Login Customer ID
                    </span>
                    <input
                      type="text"
                      value={String(editFields.metadata['loginCustomerId'] ?? '')}
                      onChange={(e) => updateMetaField('loginCustomerId', e.target.value)}
                      className="mt-0.5 w-full text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Manager Account ID
                    </span>
                    <input
                      type="text"
                      value={String(editFields.metadata['managerAccountId'] ?? '')}
                      onChange={(e) => updateMetaField('managerAccountId', e.target.value)}
                      className="mt-0.5 w-full text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
                    />
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editFields.metadata['hasManagerAccount'] === true}
                      onChange={(e) => updateMetaField('hasManagerAccount', e.target.checked)}
                      className="rounded border-gray-300 dark:border-gray-700"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-200">
                      Has Manager Account
                    </span>
                  </label>
                </div>
              )}

              {/* Generic metadata editor for non-Google-Ads-specific keys */}
              {Object.entries(editFields.metadata)
                .filter(([key]) => {
                  if (!isGoogleAds) return true;
                  return !['loginCustomerId', 'managerAccountId', 'hasManagerAccount'].includes(
                    key,
                  );
                })
                .map(([key, val]) => (
                  <label key={key} className="block">
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      {key}
                    </span>
                    <input
                      type="text"
                      value={String(val ?? '')}
                      onChange={(e) => updateMetaField(key, e.target.value)}
                      className="mt-0.5 w-full text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
                    />
                  </label>
                ))}

              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-900 dark:bg-gray-950 text-white hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
                >
                  {isSaving ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Save className="w-3 h-3" />
                  )}
                  Save
                </button>
                <button
                  onClick={onCancelEdit}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <X className="w-3 h-3" />
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Google Ads reselect modal */}
          {isGoogleAds && showReselect && !isEditing && (
            <GoogleAdsReselectModal
              storeId={storeId}
              currentAccountId={c.externalAccountId}
              onClose={() => setShowReselect(false)}
            />
          )}

          {/* Actions bar */}
          {!isEditing && (
            <div className="flex items-center gap-2 pt-1 border-t border-gray-200 dark:border-gray-800">
              <button
                onClick={onStartEdit}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-900 transition-colors"
              >
                <Edit3 className="w-3 h-3" />
                Edit
              </button>
              <button
                onClick={onRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-900 disabled:opacity-50 transition-colors"
              >
                {isRefreshing ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3" />
                )}
                Refresh Token
              </button>
              <button
                onClick={onReimport}
                disabled={isReimporting}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-blue-200 dark:border-blue-900/50 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 disabled:opacity-50 transition-colors"
              >
                {isReimporting ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Download className="w-3 h-3" />
                )}
                Reimport from Legacy
              </button>
              {isGoogleAds && (
                <button
                  onClick={() => setShowReselect(true)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-indigo-200 dark:border-indigo-900/50 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Re-select Account
                </button>
              )}
              <button
                onClick={onToggleActive}
                disabled={isToggling}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 ${
                  c.isActive
                    ? 'border-amber-200 text-amber-700 dark:text-amber-300 hover:bg-amber-50'
                    : 'border-green-200 text-green-700 dark:text-green-400 hover:bg-green-50'
                }`}
              >
                {isToggling && <Loader2 className="w-3 h-3 animate-spin" />}
                {c.isActive ? 'Deactivate' : 'Activate'}
              </button>
              <button
                onClick={onDelete}
                disabled={isDeleting}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-50 transition-colors ml-auto"
              >
                {isDeleting ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Trash2 className="w-3 h-3" />
                )}
                Remove
              </button>
              <button
                onClick={onHardDelete}
                disabled={isDeleting}
                title="Permanently delete the row (bypasses soft-delete). Use when the store can't reconnect because of a duplicate-key error."
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-red-600 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {isDeleting ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Trash2 className="w-3 h-3" />
                )}
                Hard delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Detail Row ─────────────────────────────────

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-gray-400 dark:text-gray-500 shrink-0">{label}:</span>
      <span className="text-gray-700 dark:text-gray-200 font-medium truncate">{value}</span>
    </div>
  );
}

// ─── Google Ads Reselect Section ────────────────

interface AccessibleAccount {
  id: string;
  name: string;
  isManager: boolean;
  parentManagerIds: string[];
}

interface ReselectCatalogResponse {
  accounts: AccessibleAccount[];
  currentSelection: {
    externalAccountId: string | null;
    loginCustomerId: string | null;
    managerAccountId: string | null;
    managerAccountName: string | null;
  };
}

function GoogleAdsReselectModal({
  storeId,
  currentAccountId,
  onClose,
}: {
  storeId: number;
  currentAccountId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string>('');
  const [selectedManagerId, setSelectedManagerId] = useState<string>('');
  const [query, setQuery] = useState<string>('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null,
  );

  const catalogQuery = useQuery({
    queryKey: ['admin', 'stores', storeId, 'google-ads', 'accounts'],
    queryFn: async ({ signal }) => {
      const res = await api.get<ReselectCatalogResponse>(
        `/api/v1/admin/stores/${storeId}/google-ads/accounts`,
        { signal },
      );
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load accounts');
      return res.data;
    },
    retry: false,
  });

  const reselectMutation = useMutation({
    mutationFn: async (body: {
      accountId: string;
      isUnderManagerAccount: boolean;
      managerAccountId: string | null;
      managerAccountName: string | null;
      accountName: string | null;
    }) => {
      const res = await api.post(`/api/v1/admin/stores/${storeId}/google-ads/reselect`, body);
      if (!res.success) throw new Error(res.error ?? 'Failed to update account');
      return res;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId, 'connections'] });
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'stores', storeId, 'google-ads', 'accounts'],
      });
      setFeedback({
        type: 'success',
        message: 'Account updated. The next data fetch should succeed.',
      });
      setTimeout(onClose, 1500);
    },
    onError: (err: Error) => setFeedback({ type: 'error', message: err.message }),
  });

  const accounts = useMemo(() => catalogQuery.data?.accounts ?? [], [catalogQuery.data]);
  const nonManagers = useMemo(() => accounts.filter((a) => !a.isManager), [accounts]);
  const currentNormalizedId = currentAccountId.replace(/-/g, '');

  const filteredAccounts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return nonManagers;
    const digitsOnly = q.replace(/\D/g, '');
    return nonManagers.filter((a) => {
      if (a.name.toLowerCase().includes(q)) return true;
      if (digitsOnly && a.id.includes(digitsOnly)) return true;
      return false;
    });
  }, [nonManagers, query]);

  const selected = nonManagers.find((a) => a.id === selectedId);
  const availableManagers = selected ? selected.parentManagerIds : [];

  useEffect(() => {
    if (!catalogQuery.data || selectedId) return;
    const current = nonManagers.find((a) => a.id === currentNormalizedId);
    if (current) {
      setSelectedId(current.id);
      setSelectedManagerId(current.parentManagerIds[0] ?? '');
    }
  }, [catalogQuery.data, currentNormalizedId, nonManagers, selectedId]);

  // Esc to close
  useEffect(() => {
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  function handleSubmit(): void {
    if (!selected) return;
    const managerName = selectedManagerId
      ? (accounts.find((a) => a.id === selectedManagerId)?.name ?? null)
      : null;
    reselectMutation.mutate({
      accountId: selected.id,
      isUnderManagerAccount: !!selectedManagerId,
      managerAccountId: selectedManagerId || null,
      managerAccountName: managerName,
      accountName: selected.name ?? null,
    });
  }

  function formatCustomerId(id: string): string {
    if (id.length !== 10) return id;
    return `${id.slice(0, 3)}-${id.slice(3, 6)}-${id.slice(6)}`;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reselect-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-950/40 flex items-center justify-center">
              <RefreshCw className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h3
                id="reselect-title"
                className="text-sm font-semibold text-gray-900 dark:text-gray-100"
              >
                Re-select Google Ads account
              </h3>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">
                Current: {formatCustomerId(currentNormalizedId)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col px-4 py-3 space-y-3">
          {catalogQuery.isLoading && (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" />
              Discovering accessible accounts…
            </div>
          )}

          {catalogQuery.error && (
            <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 rounded-lg p-2.5">
              {(catalogQuery.error as Error).message}
            </div>
          )}

          {feedback && (
            <div
              className={`text-xs rounded-lg p-2.5 border ${
                feedback.type === 'success'
                  ? 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900/40'
                  : 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/40'
              }`}
            >
              {feedback.message}
            </div>
          )}

          {!catalogQuery.isLoading && !catalogQuery.error && (
            <>
              {nonManagers.length === 0 ? (
                <div className="text-xs text-gray-500 dark:text-gray-400 py-8 text-center">
                  No ad accounts found. The user may need to re-authorize.
                </div>
              ) : (
                <>
                  {/* Search */}
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search by account name or ID…"
                      autoFocus
                      className="w-full pl-8 pr-8 py-2 text-xs border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 placeholder:text-gray-400"
                    />
                    {query && (
                      <button
                        onClick={() => setQuery('')}
                        aria-label="Clear search"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  <div className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center justify-between">
                    <span>
                      {filteredAccounts.length} of {nonManagers.length} account
                      {nonManagers.length === 1 ? '' : 's'}
                    </span>
                    <button
                      onClick={() => catalogQuery.refetch()}
                      disabled={catalogQuery.isFetching}
                      className="inline-flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50"
                    >
                      {catalogQuery.isFetching ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3 h-3" />
                      )}
                      Refresh
                    </button>
                  </div>

                  {/* Accounts list */}
                  <div className="flex-1 overflow-y-auto border border-gray-200 dark:border-gray-800 rounded-lg divide-y divide-gray-100 dark:divide-gray-800 min-h-[180px] max-h-[280px]">
                    {filteredAccounts.length === 0 ? (
                      <div className="text-xs text-gray-400 dark:text-gray-500 py-8 text-center">
                        No accounts match "{query}"
                      </div>
                    ) : (
                      filteredAccounts.map((a) => {
                        const isSelected = selectedId === a.id;
                        const isCurrent = a.id === currentNormalizedId;
                        return (
                          <button
                            key={a.id}
                            onClick={() => {
                              setSelectedId(a.id);
                              setSelectedManagerId(a.parentManagerIds[0] ?? '');
                            }}
                            className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 transition-colors ${
                              isSelected
                                ? 'bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 dark:hover:bg-indigo-950/60'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-950'
                            }`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {a.name || '(unnamed)'}
                                </span>
                                {isCurrent && (
                                  <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                                    current
                                  </span>
                                )}
                                {a.parentManagerIds.length > 0 && (
                                  <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-indigo-100 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300">
                                    under MCC
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">
                                {formatCustomerId(a.id)}
                              </div>
                            </div>
                            {isSelected && (
                              <CheckCircle2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0" />
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>

                  {/* Manager dropdown */}
                  {selected && availableManagers.length > 0 && (
                    <label className="block">
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Manager Account (MCC)
                      </span>
                      <select
                        value={selectedManagerId}
                        onChange={(e) => setSelectedManagerId(e.target.value)}
                        className="mt-1 w-full text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      >
                        <option value="">— None (direct account) —</option>
                        {availableManagers.map((mid) => {
                          const m = accounts.find((a) => a.id === mid);
                          return (
                            <option key={mid} value={mid}>
                              {m?.name ?? mid} ({formatCustomerId(mid)})
                            </option>
                          );
                        })}
                      </select>
                    </label>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/50 rounded-b-xl">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selected || reselectMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {reselectMutation.isPending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Save className="w-3 h-3" />
            )}
            Save selection
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Create Connection Form ─────────────────────

function CreateConnectionForm({
  onSubmit,
  onCancel,
  isPending,
}: {
  onSubmit: (body: ConnectionCreateBody) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [platform, setPlatform] = useState<PlatformKey>('shopify');
  const [accessToken, setAccessToken] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [externalAccountId, setExternalAccountId] = useState('');
  const [currency, setCurrency] = useState('');

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body: ConnectionCreateBody = {
      platform,
      accessToken,
    };
    if (refreshToken.trim()) body.refreshToken = refreshToken.trim();
    if (externalAccountId.trim()) body.externalAccountId = externalAccountId.trim();
    if (currency.trim()) body.currency = currency.trim();
    onSubmit(body);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 space-y-3 bg-gray-50 dark:bg-gray-950"
    >
      <div className="text-xs font-medium text-gray-900 dark:text-gray-100 mb-1">
        New Connection
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Platform
          </span>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value as PlatformKey)}
            className="mt-0.5 w-full text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
          >
            {PLATFORM_KEYS.map((pk) => (
              <option key={pk} value={pk}>
                {PLATFORM_META[pk]?.label ?? pk}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Currency
          </span>
          <input
            type="text"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            placeholder="USD"
            className="mt-0.5 w-24 text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
          />
        </label>
      </div>
      <label className="block">
        <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Access Token *
        </span>
        <input
          type="text"
          value={accessToken}
          onChange={(e) => setAccessToken(e.target.value)}
          required
          className="mt-0.5 w-full text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
        />
      </label>
      <label className="block">
        <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Refresh Token
        </span>
        <input
          type="text"
          value={refreshToken}
          onChange={(e) => setRefreshToken(e.target.value)}
          className="mt-0.5 w-full text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
        />
      </label>
      <label className="block">
        <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          External Account ID
        </span>
        <input
          type="text"
          value={externalAccountId}
          onChange={(e) => setExternalAccountId(e.target.value)}
          className="mt-0.5 w-full text-xs px-2 py-1.5 border border-gray-200 dark:border-gray-800 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900"
        />
      </label>
      <div className="flex items-center gap-2 pt-1">
        <button
          type="submit"
          disabled={isPending || !accessToken.trim()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-900 dark:bg-gray-950 text-white hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
        >
          {isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          Create
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <X className="w-3 h-3" />
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Onboarding Control ─────────────────────────

function OnboardingControl({
  storeId,
  step,
  onboardedAt,
}: {
  storeId: number;
  step: number;
  onboardedAt: string | null;
}) {
  const queryClient = useQueryClient();
  const isOnboarded = !!onboardedAt;

  const mutation = useMutation({
    mutationFn: async (body: { step?: number; completed?: boolean }) => {
      const res = await api.patch(`/api/v1/admin/stores/${storeId}/onboarding`, body);
      if (!res.success) throw new Error(res.error ?? 'Failed to update');
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stores', storeId] });
    },
  });

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CheckCircle2
            className={`w-4 h-4 ${isOnboarded ? 'text-green-500' : 'text-amber-500'}`}
          />
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Onboarding</h4>
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${
              isOnboarded
                ? 'bg-green-50 text-green-700 dark:text-green-400 border-green-200'
                : 'bg-amber-50 text-amber-700 dark:text-amber-300 border-amber-200'
            }`}
          >
            {isOnboarded ? 'Complete' : `Step ${step}/4`}
          </span>
        </div>
      </div>

      {/* Step selector */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-gray-500 dark:text-gray-400 w-20 shrink-0">
          Current step:
        </span>
        <div className="flex gap-1">
          {[0, 1, 2, 3, 4].map((s) => (
            <button
              key={s}
              onClick={() => mutation.mutate({ step: s })}
              disabled={mutation.isPending}
              className={`w-8 h-8 rounded-lg text-xs font-semibold border transition-all ${
                s === step
                  ? 'bg-gray-900 text-white border-gray-900'
                  : s < step
                    ? 'bg-green-50 text-green-700 dark:text-green-400 border-green-200 dark:border-green-900/50 hover:bg-green-100'
                    : 'bg-white text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-950 hover:text-gray-600'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1">
          (0=not started, 4=complete)
        </span>
      </div>

      {/* Quick actions */}
      <div className="flex items-center gap-2">
        {!isOnboarded ? (
          <button
            onClick={() => mutation.mutate({ completed: true })}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Mark as Onboarded
          </button>
        ) : (
          <button
            onClick={() => mutation.mutate({ completed: false })}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            Reset Onboarding
          </button>
        )}
        {onboardedAt && (
          <span className="text-[10px] text-gray-400 dark:text-gray-500">
            Onboarded {formatDate(onboardedAt)}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Small components ───────────────────────────

function MetaCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2">
      <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {label}
      </div>
      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{value}</div>
    </div>
  );
}
